// ============================================================================
// Printable scorecard / employer checklist.
// ============================================================================
// Deliberately dependency-free. The obvious move is jsPDF + html2canvas, but that
// is ~300 KB added to a PUBLIC widget bundle that already ships at ~325 KB
// gzipped, to produce a worse-looking document than the browser's own print
// engine. Instead we open a self-contained print document and call print(), which
// gives the user "Save as PDF" natively on every desktop browser and the system
// share sheet on mobile.
//
// The window is opened synchronously inside the click handler — deferring it
// behind an await is what trips popup blockers.
// ============================================================================

const PRINT_STYLES = `
  @page { margin: 16mm 14mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    color: #1a2332;
    font-size: 11pt;
    line-height: 1.5;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  h1 { font-size: 19pt; margin: 0 0 4px; }
  h2 { font-size: 13pt; margin: 22px 0 8px; padding-bottom: 5px; border-bottom: 1.5px solid #e6ebf1; }
  h3 { font-size: 11.5pt; margin: 16px 0 6px; }
  .sub { color: #6b7a8d; font-size: 9.5pt; margin: 0 0 4px; }
  .brand { font-size: 9pt; letter-spacing: 1px; text-transform: uppercase; color: #8a7250; margin-bottom: 14px; }

  /* Letterhead — the document goes to employers, lawyers and IRCC-adjacent
     readers, so it should look like it came from the practice, not from a
     browser print dialog. */
  .letterhead {
    display: flex;
    align-items: center;
    gap: 14px;
    padding-bottom: 12px;
    margin-bottom: 18px;
    border-bottom: 2.5px solid #1a2332;
  }
  .letterhead img { max-height: 46px; max-width: 190px; width: auto; }
  .letterhead .who { flex: 1; }
  .letterhead .name { font-size: 14pt; font-weight: 700; line-height: 1.2; }
  .letterhead .role { font-size: 9pt; color: #6b7a8d; margin-top: 2px; }
  .letterhead .contact { font-size: 8.5pt; color: #6b7a8d; text-align: right; line-height: 1.5; white-space: nowrap; }
  .letterhead .contact div { margin-bottom: 1px; }
  .prepared { font-size: 8.5pt; color: #94a3b8; margin: -8px 0 16px; }
  .verdict { font-weight: 700; font-size: 12pt; margin: 14px 0; }
  .score { font-size: 40pt; font-weight: 800; line-height: 1; }
  .score-max { font-size: 9.5pt; color: #6b7a8d; }
  .noscore { padding: 10px 12px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 5px; font-size: 10pt; }
  table { width: 100%; border-collapse: collapse; margin: 6px 0 0; }
  td { padding: 5px 0; border-bottom: 1px solid #eef2f6; vertical-align: top; }
  td.value { text-align: right; font-weight: 700; white-space: nowrap; width: 90px; font-variant-numeric: tabular-nums; }
  td.muted { color: #94a3b8; }
  ul { margin: 4px 0 0; padding-left: 18px; }
  li { margin-bottom: 7px; }
  li .detail { display: block; font-size: 9.5pt; color: #64748b; margin-top: 2px; }
  .check { list-style: none; padding-left: 0; }
  .check li { padding-left: 22px; position: relative; }
  .check li::before {
    content: ''; position: absolute; left: 0; top: 2px;
    width: 12px; height: 12px; border: 1.5px solid #94a3b8; border-radius: 2px;
  }
  .status-pass::before { content: '\\2713'; border: none !important; color: #16a34a; font-weight: 700; }
  .status-warn::before { content: '!'; border: none !important; color: #d97706; font-weight: 700; }
  .status-fail::before { content: '\\2715'; border: none !important; color: #dc3545; font-weight: 700; }
  .legal { margin-top: 26px; padding-top: 12px; border-top: 1px solid #e6ebf1; font-size: 8.5pt; color: #94a3b8; }
  @media print { .noprint { display: none !important; } }
  .noprint { text-align: center; margin-bottom: 18px; }
  .noprint button {
    padding: 9px 20px; font-size: 11pt; font-weight: 700; cursor: pointer;
    border: none; border-radius: 6px; background: #1a2332; color: #fff;
  }
`;

const escapeHtml = value => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

function openPrintWindow(title, bodyHtml) {
  // Opened synchronously so the browser attributes it to the user's click.
  const printWindow = window.open('', '_blank', 'width=880,height=1000');
  if (!printWindow) return false;

  printWindow.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(title)}</title>
  <style>${PRINT_STYLES}</style>
</head>
<body>
  <div class="noprint"><button onclick="window.print()">Save as PDF / Print</button></div>
  ${bodyHtml}
</body>
</html>`);
  printWindow.document.close();
  printWindow.focus();
  return true;
}

const STATUS_CLASS = { pass: 'status-pass', warn: 'status-warn', fail: 'status-fail' };

/**
 * Practice letterhead. `brand` comes from the widget's expertData, so the
 * document carries whichever adviser's widget produced it rather than a generic
 * header — these get forwarded to employers and kept on file.
 *
 * Everything is optional: with no brand data the block is skipped entirely
 * rather than rendering an empty frame.
 */
function renderLetterhead(brand, tr) {
  if (!brand) return '';
  const { logoUrl, expertName, expertTitle, site, email, phone, location } = brand;
  if (!logoUrl && !expertName) return '';

  const contact = [site, email, phone, location]
    .filter(Boolean)
    .map(v => `<div>${escapeHtml(v)}</div>`)
    .join('');

  return `
  <div class="letterhead">
    ${logoUrl ? `<img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(expertName || '')}">` : ''}
    <div class="who">
      ${expertName ? `<div class="name">${escapeHtml(expertName)}</div>` : ''}
      ${expertTitle ? `<div class="role">${escapeHtml(expertTitle)}</div>` : ''}
    </div>
    ${contact ? `<div class="contact">${contact}</div>` : ''}
  </div>`;
}

/** "Prepared by X on <date>" line under the letterhead. */
function renderPreparedLine(brand) {
  const today = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const who = brand?.expertName ? `Prepared by ${escapeHtml(brand.expertName)} · ` : '';
  return `<p class="prepared">${who}${escapeHtml(today)}</p>`;
}

/**
 * Candidate scorecard: eligibility, the factor breakdown, the boosts and the
 * deadline chain. Publishes a score only when the result did.
 */
export function printScorecard({ result, tr, deadlines, brand }) {
  if (!result) return false;

  const gates = (result.gates || []).map(gate => `
    <li class="${STATUS_CLASS[gate.status] || ''}">${escapeHtml(tr(GATE_LABEL_KEYS[gate.id] || gate.id))}
      ${gate.note ? `<span class="detail">${escapeHtml(tr(gate.note))}</span>` : ''}
    </li>`).join('');

  const factors = (result.breakdown || []).map(factor => {
    let value;
    if (factor.known) value = `${factor.points} / ${factor.max}`;
    else if (factor.notApplicable) value = escapeHtml(tr('factorNotApplicable'));
    else value = `— / ${factor.max}`;
    return `
    <tr>
      <td>${escapeHtml(tr(factor.labelKey))}</td>
      <td class="value ${factor.known ? '' : 'muted'}">${value}</td>
    </tr>`;
  }).join('');

  const boosts = (result.boosts || []).map(boost => `
    <tr>
      <td>${escapeHtml(tr(boost.key, boost.params))}</td>
      <td class="value">${escapeHtml(tr('boostPoints', { gain: boost.gain }))}</td>
    </tr>`).join('');

  const deadlineRows = (deadlines || []).map((deadline, index) => `
    <li>${String(index + 1).padStart(2, '0')} &nbsp;${escapeHtml(tr(deadline.textKey))}</li>`).join('');

  const scoreBlock = result.total != null
    ? `<div class="score">${result.total}</div>
       <div class="score-max">${escapeHtml(tr('scoreOf', { max: result.maxScore }))}</div>`
    : `<div class="noscore"><strong>${escapeHtml(tr('scoreUnavailableTitle'))}</strong><br>${
        escapeHtml(tr('scoreUnavailableBody', { count: (result.missingFactors || []).length }))
      }</div>`;

  return openPrintWindow(tr('resultsTitle'), `
    ${renderLetterhead(brand, tr)}
    <div class="brand">${escapeHtml(tr('resultsTitle'))}</div>
    <h1>${escapeHtml(tr('scoreHeading'))}</h1>
    <p class="sub">${escapeHtml(tr('resultsSubtitle', { legalBasis: result.legalBasis, version: result.rulesVersion }))}</p>
    ${renderPreparedLine(brand)}

    ${scoreBlock}

    <h2>${escapeHtml(tr('eligibilityHeading'))}</h2>
    <p class="verdict">${escapeHtml(tr(VERDICT_KEYS[result.verdict] || 'verdictNeedsVerification'))}</p>
    <ul class="check">${gates}</ul>

    ${factors ? `<h2>${escapeHtml(tr('breakdownHeading'))}</h2><table>${factors}</table>` : ''}
    ${boosts ? `<h2>${escapeHtml(tr('boostHeading'))}</h2><table>${boosts}</table>` : ''}
    ${deadlineRows ? `<h2>${escapeHtml(tr('deadlinesHeading'))}</h2><ul>${deadlineRows}</ul>` : ''}

    <p class="legal">${escapeHtml(tr('disclaimer'))}</p>
  `);
}

/**
 * Employer checklist: the verdict plus the complete four-phase to-do list, so the
 * candidate can hand their employer something they can actually work through.
 * `phases` is passed in rather than hardcoded here so the copy has one home.
 */
export function printEmployerChecklist({ result, tr, phases, brand }) {
  const requirements = result
    ? (result.items || []).map(item => `
      <li class="${STATUS_CLASS[item.status] || ''}">${escapeHtml(tr(EMPLOYER_REQUIREMENT_KEYS[item.id] || item.id))}
        ${item.reasonKey ? `<span class="detail">${escapeHtml(tr(item.reasonKey))}</span>` : ''}
      </li>`).join('')
    : '';

  const tierNote = result?.tier
    ? `<p class="sub">${escapeHtml(tr('empTierNote', {
        revenueFloor: result.tier.revenueFloor,
        minEmployees: result.tier.minEmployees,
      }))}</p>`
    : '';

  const phaseBlocks = (phases || []).map(phase => `
    <h3>${escapeHtml(phase.title)}</h3>
    <ul class="check">
      ${phase.items.map(item => `<li>${escapeHtml(item.text)}${
        item.detail ? `<span class="detail">${escapeHtml(item.detail)}</span>` : ''
      }</li>`).join('')}
    </ul>`).join('');

  return openPrintWindow(tr('employerTitle'), `
    ${renderLetterhead(brand, tr)}
    <div class="brand">${escapeHtml(tr('employerCtaHeading'))}</div>
    <h1>${escapeHtml(tr('employerTitle'))}</h1>
    <p class="sub">${escapeHtml(tr('employerCtaBody'))}</p>
    ${renderPreparedLine(brand)}

    ${result ? `<p class="verdict">${escapeHtml(tr(EMPLOYER_VERDICT_KEYS[result.verdict] || 'empVerdictNeedsVerification'))}</p>` : ''}
    ${requirements ? `<ul class="check">${requirements}</ul>` : ''}
    ${tierNote}

    ${phaseBlocks ? `<h2>${escapeHtml(tr('employerChecklistHeading'))}</h2>${phaseBlocks}` : ''}

    <p class="legal">${escapeHtml(tr('disclaimer'))}</p>
  `);
}

const VERDICT_KEYS = {
  'appears-eligible': 'verdictEligible',
  'needs-verification': 'verdictNeedsVerification',
  'not-eligible': 'verdictNotEligible',
  'needs-job-offer': 'verdictNeedsJobOffer',
};

const EMPLOYER_VERDICT_KEYS = {
  'appears-eligible': 'empVerdictEligible',
  'needs-verification': 'empVerdictNeedsVerification',
  'not-eligible': 'empVerdictNotEligible',
};

const GATE_LABEL_KEYS = {
  'work-experience': 'gateExperience',
  'language-minimum': 'gateLanguage',
  'post-secondary-credential': 'gateCredential',
  'eca': 'gateEca',
  'legal-status': 'gateStatus',
  'ohip-billing': 'gateOhip',
  'cpso-certificate': 'gateCpso',
  'ontario-practice': 'gateOntarioPractice',
};

const EMPLOYER_REQUIREMENT_KEYS = {
  'location': 'empReqLocation',
  'years-in-business': 'empReqYears',
  'revenue': 'empReqRevenue',
  'employees': 'empReqEmployees',
  'esa-ohsa': 'empReqOrders',
  'job-offer-shape': 'empReqFullTime',
  'wage-level': 'empReqWage',
  'recruitment-waiver': 'empReqRecruitment',
  'cvor': 'empReqCvor',
};
